import { Injectable } from '@angular/core';
import { NavController } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { clearSelectCustomerGroupAction } from '@symphony-solo/domain/customer-groups/src';
import { clearLocationAction } from '@symphony-solo/domain/location/src';
import { AlertService } from '@symphony-solo/shared/alert/src';
import { AppStateFailureBuilder, AppStateFailureItemWithErrorCode, FailureType } from '@symphony-solo/shared/common-state/src';
import { ApiResponseCode } from '@symphony-solo/shared/http/src';
import { CHECKOUT_ORDER, emailValidator, LOGIN, SELECT_APP_SIMPLE_TABS, SIGNUP_CONFIRMATION } from '@symphony-solo/shared/utils/src';
import { EMPTY, of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { LoginInfoBuilder } from '../builders';
import { AccountDetailsFlowType } from '../enum';
import {
  AccountDisabledHandler,
  AccountLockedHandler,
  HandlerChainService,
  PasswordExpiredHandler,
  SuccessHandler,
  UnknownErrorHandler,
  ValidationFailedHandler,
} from '../handlers';
import { CredentialsModel, PasswordResetRequest } from '../models';
import {
  AccountDetailsService,
  AccountService,
  AuthenticationService,
  HashService,
  ResetPasswordService,
  ValidationEmailService,
} from '../services';
import {
  authenticateAction,
  authenticateErrorAction,
  authenticateSuccessAction,
  createAccountAction,
  createAccountErrorAction,
  createAccountSuccessAction,
  getAccountPropertiesAction,
  getAccountPropertiesSuccessAction,
  getEmailDomainsAction,
  getEmailDomainsSuccessAction,
  getEmailDomainsErrorAction,
  goHomeSuccessAction,
  loadAccountDetailsAction,
  loadAccountDetailsErrorAction,
  loadAccountDetailsSuccessAction,
  logoutSuccessAction,
  personalDetailsAction,
  personalDetailsErrorAction,
  personalDetailsSuccessAction,
  sendResetPasswordEmailAction,
  sendResetPasswordEmailErrorAction,
  sendResetPasswordEmailSuccessAction,
  sendValidationEmailAction,
  sendValidationEmailErrorAction,
  sendValidationEmailSuccessAction,
  changePasswordAction,
  changePasswordSuccessAction,
  changePasswordErrorAction,
  deactivateAccountAction,
  deactivateAccountSuccessAction,
  deactivateAccountErrorAction,
  tokenForValidationSuccessAction,
  tokenForValidationErrorAction,
  AccountResetPasswordAction,
  AccountResetPasswordErrorAction,
  AccountResetPasswordSuccessAction,
  clearCartAfterFiveHrsAction,
} from './authentication.actions';
import { AuthenticationFacade } from './authentication.facade';
import { ActivatedRoute } from '@angular/router';
import { HistoryFacade } from '@symphony-solo/domain/history/src';
import { OneSignal } from '@ionic-native/onesignal/ngx';
import { AppConfigService, CompanyGroupFacade } from '@symphony-solo/domain/company-group/src';
import { ToastService } from '@symphony-solo/shared/toast/src';
import { CompanyConfigFacade } from '@symphony-solo/domain/company-config/src';
import { CartFacade } from '@symphony-solo/domain/cart/src/lib/+state/cart.facade';
import { ChooseLocationFacade } from '@symphony-solo/domain/choose-location/src';
@Injectable()
export class AuthenticationEffects {
  constructor(
    private action$: Actions,
    private authenticationService: AuthenticationService,
    private cartFacade: CartFacade,
    private authenticationFacade: AuthenticationFacade,
    private hashService: HashService,
    private navController: NavController,
    private handlerChainService: HandlerChainService,
    private accountDetailsService: AccountDetailsService,
    private validationEmailService: ValidationEmailService,
    private resetPasswordService: ResetPasswordService,
    private accountService: AccountService,
    private alertService: AlertService,
    private route: ActivatedRoute,
    private historyFacade: HistoryFacade,
    private oneSignal: OneSignal,
    private appConfigService: AppConfigService,
    // private _storage: LocalStorageService,
    // private storage: Storage,
    // private orderFacade: OrderFacade,
    private toastService: ToastService,
    private companyConfigFacade: CompanyConfigFacade,
    private companyGroupFacade: CompanyGroupFacade,
    private chooseLocationFacade: ChooseLocationFacade
  ) {}

  protected authenticate$ = createEffect(() =>
    this.action$.pipe(
      ofType(authenticateAction),
      switchMap((payload) => {
        const credentials = payload.credentials;
        if (!credentials.email || !credentials.password) {
          const appStateFailure = new AppStateFailureBuilder()
            .setFailureType(FailureType.General)
            .addFailure(new AppStateFailureItemWithErrorCode('Invalid credentials', ApiResponseCode.Account_Not_Validated))
            .build();
          return of(authenticateErrorAction({ failure: appStateFailure }));
        }

        const loginInfo = new LoginInfoBuilder()
          .setEmail(credentials.email)
          .setPassword(credentials.password)
          .setCompanyGuid(this.appConfigService.config?.companyGuid)
          .setHash(this.hashService.computeHash(this.appConfigService.config?.companyGuid, credentials.email, credentials.password))
          .build();
        return this.authenticationService.authenticate(loginInfo).pipe(
          map((response) => {
            return this.handlerChainService
              .addHandler(new SuccessHandler())
              .addHandler(new ValidationFailedHandler())
              .addHandler(new AccountLockedHandler())
              .addHandler(new PasswordExpiredHandler())
              .addHandler(new AccountDisabledHandler())
              .addHandler(new UnknownErrorHandler())
              .handle(response);
          }),
          catchError((error) => {
            // TODO: Handle error
            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode(error, ApiResponseCode.Unknown_Error))
              .build();
            return of(authenticateErrorAction({ failure }));
          })
        );
      })
    )
  );

  protected authenticationSuccess$ = createEffect(() =>
    this.action$.pipe(
      ofType(authenticateSuccessAction),
      withLatestFrom(this.cartFacade.cart$, this.cartFacade.accountID$, this.companyGroupFacade.getCompanyGroup$),
      switchMap(([action, cart, cartAccountID, companyGroup]) => {
        if (this.route.snapshot?.queryParams?.tta) {
          this.authenticationFacade.setTokenForValidation(this.route.snapshot?.queryParams?.tta);
        }

        this.authenticationFacade.isAuthenticated.next(true);

        let NAVIGATE_TO_PATH = '/';
        const ANONYMOUS_USER_ID = -1;

        return this.accountDetailsService.getAccountDetails().pipe(
          map((account) => {
            if (companyGroup?.allowPushNotifications) {
              this.oneSignal.sendTag('acc', String(account.accountID));
              this.oneSignal.sendTag('cid', companyGroup?.loyaltyCompanyID ? companyGroup.loyaltyCompanyID : '');
            }
            const isValidated = account.validated;
            this.authenticationFacade.isAccountValidated.next(isValidated);
            this.cartFacade.setAccountID(account.accountID);

            if (!isValidated) {
              return EMPTY;
            }

            if (cart && cart.length && isValidated && cartAccountID === ANONYMOUS_USER_ID) {
              NAVIGATE_TO_PATH = CHECKOUT_ORDER;
            }

            this.navController.navigateRoot(NAVIGATE_TO_PATH);
            return EMPTY;
          }),
          catchError((err) => {
            return EMPTY;
          })
        );
      }),
      map(() => loadAccountDetailsAction({ accountDetailsFlowType: AccountDetailsFlowType.Login }))
    )
  );

  public padTo2Digits(num) {
    return num.toString().padStart(2, '0');
  }

  public formatDate(date = new Date()) {
    return [date.getFullYear(), this.padTo2Digits(date.getMonth() + 1), this.padTo2Digits(date.getDate())].join('-');
  }
  public fetchTime(dateTime: string): string {
    // const time = dateTime.split('T')[1];
    const time = this.padTo2Digits(new Date(dateTime).getHours()) + ':' + this.padTo2Digits(new Date(dateTime).getMinutes());
    return time;
  }

  // protected softCheckAuthentication$ = createEffect(() =>
  //   this.action$.pipe(
  //     ofType(softCheckAuthenticationAction),
  //     switchMap(() => {
  //       return this.authenticationService.softCheck().pipe(
  //         map(() => softCheckAuthenticationSuccessAction()),
  //         catchError(() => {
  //           //guest login
  //           return [softCheckAuthenticationErrorAction(), logoutSuccessAction()];
  //         })
  //       );
  //     })
  //   )
  // );

  protected loadAccountDetails$ = createEffect(() =>
    this.action$.pipe(
      ofType(loadAccountDetailsAction),
      switchMap(({ accountDetailsFlowType }) => {
        return this.accountDetailsService.getAccountDetails().pipe(
          map((accountDetails) => loadAccountDetailsSuccessAction({ accountDetails, accountDetailsFlowType })),
          catchError(() => {
            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode('Error on loading Account Details', ApiResponseCode.Server_Error))
              .build();
            return of(loadAccountDetailsErrorAction({ failure }));
          })
        );
      })
    )
  );
  //it will cause infinite loading (memory leaks)

  // protected loadAccountDetailsError$ = createEffect(() =>
  //   this.action$.pipe(
  //     ofType(loadAccountDetailsErrorAction)
  //   )
  // );

  protected loadAccountDetailsSuccess$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(loadAccountDetailsSuccessAction),
        switchMap((accountDetailsAction) => {
          const { accountDetails, accountDetailsFlowType } = accountDetailsAction;
          const resendValidationEmailButton = {
            text: 'Resend Validation Email',
            cssClass: 'okButton centerButton',
            handler: () => {
              this.authenticationFacade.sendValidationEmail();
            },
          };
          const dismissButton = {
            text: 'Dismiss',
            cssClass: 'cancelButton centerButton',
            handler: () => {
              this.authenticationFacade.logoutSuccess();
            },
          };

          if (!accountDetails.validated) {
            if (this.authenticationFacade.getTokenForValidation()) {
              return this.accountService.validateAccount(this.authenticationFacade.getTokenForValidation()).pipe(
                tap(() => {
                  this.alertService.presentCustomAlert('Validated!', 'Your account has been successfully validated.', 'Celeberate', 'h-45');
                  this.toastService.dismissToast();
                }),
                map(() => tokenForValidationSuccessAction()),
                catchError((err) => {
                  this.alertService.presentAlert(
                    'Validation Failed',
                    'Sorry unable to validate your account. If you would like to resend the validation email press the button below.',
                    [resendValidationEmailButton, dismissButton],
                    false
                  );
                  return [tokenForValidationErrorAction()];
                })
              );
            } else {
              if (!location.pathname.includes('choose-location') && location.pathname !== 'app/choose-function') {
                this.navController.navigateRoot('/');
              }
              this.alertService.presentAlert(
                'Account not validated',
                'Your account has not been validated. You must validate your email address before you can log in. Please check your email (including the junk folder). If you would like to resend the validation email press the button below.',
                [resendValidationEmailButton, dismissButton],
                false
              );
              return EMPTY;
            }
          } else if (accountDetailsFlowType === AccountDetailsFlowType.Login) {
            return EMPTY;
            // this.authenticationFacade.isAuthenticated.next(true);
            // this.navController.navigateRoot(SELECT_APP);
          } else {
            return EMPTY;
          }
        })
      ),
    { dispatch: false }
  );

  protected logoutSuccess$ = createEffect(() =>
    this.action$.pipe(
      ofType(logoutSuccessAction),
      tap((action) => {
        this.companyConfigFacade.clearQRScanData();
        this.historyFacade.clearState('history');
        if(action.withNavigation) {
          this.oneSignal.deleteTag('acc');
          this.oneSignal.deleteTag('cid');
          this.navController.navigateRoot(LOGIN);
        }
      }),
      switchMap(() => of(clearSelectCustomerGroupAction(), clearLocationAction()))
    )
  );

  protected goHomeSuccess$ = createEffect(() =>
    this.action$.pipe(
      ofType(goHomeSuccessAction),
      tap(() => {
        if (!location.pathname.includes('login')) {
          this.authenticationFacade.goHomebuttonPressed.next(true);
        }
        this.navController.navigateRoot(SELECT_APP_SIMPLE_TABS);
      }),
      switchMap(() => of(clearSelectCustomerGroupAction(), clearLocationAction()))
    )
  );

  protected sendValidationEmail$ = createEffect(() =>
    this.action$.pipe(
      ofType(sendValidationEmailAction),
      switchMap(() => {
        return this.validationEmailService.sendValidationEmail().pipe(
          map(() => sendValidationEmailSuccessAction()),
          catchError(() => {
            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode('Error on sending validation email', ApiResponseCode.Server_Error))
              .build();
            return of(sendValidationEmailErrorAction({ failure }));
          }),
          tap(() => {
            this.authenticationFacade.logoutSuccess();
          })
        );
      })
    )
  );

  protected sendValidationEmailSuccess$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(sendValidationEmailSuccessAction),
        tap(() => {
          const okButton = {
            text: 'OK',
            cssClass: 'okButton centerButton',
          };
          this.alertService.presentAlert(
            'Validation Email Sent',
            'A validation email has been sent to the email address you provided. Please allow up to 10 minutes and check all email folders. Once validated you can log in.',
            [okButton]
          );
        })
      ),
    { dispatch: false }
  );

  protected sendResetPasswordEmail$ = createEffect(() =>
    this.action$.pipe(
      ofType(sendResetPasswordEmailAction),
      switchMap((payload) => {
        const companyGuid = this.appConfigService.config?.companyGuid;
        const email = payload.email;
        const includeResetCode = true;

        const emailValidation = emailValidator(email);
        if (!email || !emailValidation) {
          const appStateFailure = new AppStateFailureBuilder()
            .setFailureType(FailureType.General)
            .addFailure(new AppStateFailureItemWithErrorCode('Invalid Email address', ApiResponseCode.Invalid_email_address))
            .build();
          return of(sendResetPasswordEmailErrorAction({ failure: appStateFailure }));
        }

        const passwordResetRequest = new PasswordResetRequest(companyGuid, email, includeResetCode);
        return this.resetPasswordService.resetPassword(passwordResetRequest).pipe(
          map(() => sendResetPasswordEmailSuccessAction()),
          catchError(() => {
            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(
                new AppStateFailureItemWithErrorCode('Error on sending reset password email request', ApiResponseCode.Server_Error)
              )
              .build();
            return of(sendResetPasswordEmailErrorAction({ failure }));
          })
        );
      })
    )
  );

  protected sendResetPasswordEmailSuccess$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(sendResetPasswordEmailSuccessAction),
        tap(() => {
          this.authenticationFacade.setResetPasswordRequest(true);
          // this.navController.navigateForward(RESET_PASSWORD_CONFIRMATION);
        })
      ),
    {
      dispatch: false,
    }
  );

  protected accountResetPassword$ = createEffect(() =>
    this.action$.pipe(
      ofType(AccountResetPasswordAction),
      switchMap((payload) => {
        return this.resetPasswordService.resetAccountPassword(payload.payload).pipe(
          map(() => AccountResetPasswordSuccessAction()),
          catchError((err) => {
            if (err?.error) {
              const failureServer = new AppStateFailureBuilder()
                .setFailureType(FailureType.API)
                .addFailure(new AppStateFailureItemWithErrorCode(err?.error, ApiResponseCode.Server_Error))
                .build();
              const okButton = {
                text: 'OK',
                cssClass: 'okButton centerButton',
              };
              this.alertService.presentAlert('Server Error', err?.error, [okButton]);
              return of(AccountResetPasswordErrorAction({ failure: failureServer }));
            } else {
              const failure = new AppStateFailureBuilder()
                .setFailureType(FailureType.API)
                .addFailure(
                  new AppStateFailureItemWithErrorCode('Error on sending account reset password request', ApiResponseCode.Server_Error)
                )
                .build();
              return of(AccountResetPasswordErrorAction({ failure }));
            }
          })
        );
      })
    )
  );

  protected accountResetPasswordSuccess$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(AccountResetPasswordSuccessAction),
        tap(() => {
          this.authenticationFacade.setAccountResetPasswordRequest(true);
          this.navController.navigateRoot(LOGIN);
        })
      ),
    {
      dispatch: false,
    }
  );

  protected createAccount$ = createEffect(() =>
    this.action$.pipe(
      ofType(createAccountAction),
      switchMap((payload) => {
        const companyGuid = this.appConfigService.config?.companyGuid;
        const account = {
          companyGuid,
          ...payload.createAccount,
        };

        // const createAccountRequest = new CreateAccountRequest(companyGuid, account);
        return this.accountService.createAccount(account).pipe(
          tap(() => {
            if (this.authenticationFacade.autoValidation === 0) {
              const credentials = new CredentialsModel(payload.createAccount.email, payload.createAccount.password);
              this.authenticationFacade.authenticate(credentials);
              // if (res.validated === true) {
              //   //its auto validated
              // } else if (res.validated === false) {
              //   this.navController.navigateForward(LOGIN);
              //   // we need to discuss this
              // }
            } else if (this.authenticationFacade.autoValidation === 1) {
              this.navController.navigateForward(SIGNUP_CONFIRMATION);
            }
          }),
          map(() => createAccountSuccessAction()),
          catchError((res) => {
            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode(res.error, ApiResponseCode.Server_Error))
              .build();
            return of(createAccountErrorAction({ failure }));
          })
        );
      })
    )
  );

  // protected createAccountSuccess$ = createEffect(
  //   () =>
  //     this.action$.pipe(
  //       ofType(createAccountSuccessAction),
  //       tap((res) => {
  //         // this.navController.navigateForward(SIGNUP_CONFIRMATION);
  //       })
  //     ),
  //   {
  //     dispatch: false,
  //   }
  // );

  protected personalDetails$ = createEffect(() =>
    this.action$.pipe(
      ofType(personalDetailsAction),
      switchMap((payload) => {
        // const createAccountRequest = new CreateAccountRequest(companyGuid, account);
        return this.accountDetailsService.personalDetails(payload.personalDetails).pipe(
          map(() => personalDetailsSuccessAction()),
          catchError((res) => {
            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode(res.error, ApiResponseCode.Server_Error))
              .build();
            return of(personalDetailsErrorAction({ failure }));
          })
        );
      })
    )
  );

  protected changePassword$ = createEffect(() =>
    this.action$.pipe(
      ofType(changePasswordAction),
      switchMap((payload) => {
        return this.accountDetailsService.changePassword(payload.changePassword).pipe(
          map((responseCode) => {
            return changePasswordSuccessAction({ responseCode });
          }),
          catchError((res) => {
            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode(res.error, ApiResponseCode.Server_Error))
              .build();
            return of(changePasswordErrorAction({ failure }));
          })
        );
      })
    )
  );

  // protected changePasswordSuccess$ = createEffect(
  //   () =>
  //     this.action$.pipe(
  //       ofType(changePasswordSuccessAction),
  //       tap(() => {
  //         this.authenticationFacade.logoutSuccess();
  //       })
  //     ),
  //   {
  //     dispatch: false,
  //   }
  // );

  protected getAccountProperties$ = createEffect(() =>
    this.action$.pipe(
      ofType(getAccountPropertiesAction),
      switchMap(() => {
        return this.accountService.getAccountProperties().pipe(
          map((accountProperties) => getAccountPropertiesSuccessAction({ accountProperties })),
          catchError(() => {
            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode('Error on loading Account Properties', ApiResponseCode.Server_Error))
              .build();
            return of(loadAccountDetailsErrorAction({ failure }));
          })
        );
      })
    )
  );

  protected getEmailDomains$ = createEffect(() =>
    this.action$.pipe(
      ofType(getEmailDomainsAction),
      switchMap(() => {
        return this.accountService.getEmailDomains().pipe(
          map((emailDomains) => getEmailDomainsSuccessAction({ emailDomains })),
          catchError(() => {
            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode('Error on loading email domains', ApiResponseCode.Server_Error))
              .build();
            return of(getEmailDomainsErrorAction({ failure }));
          })
        );
      })
    )
  );

  protected deactivateAccount$ = createEffect(() =>
    this.action$.pipe(
      ofType(deactivateAccountAction),
      switchMap(() => {
        return this.accountDetailsService.deactivateAccount().pipe(
          map(() => deactivateAccountSuccessAction()),
          catchError(() => {
            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode('Error on delete account', ApiResponseCode.Server_Error))
              .build();
            return of(deactivateAccountErrorAction({ failure }));
          })
        );
      })
    )
  );

  protected clearCartAfterFiveHrs$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(clearCartAfterFiveHrsAction),
        withLatestFrom(
          this.cartFacade.cartStartTime$,
          this.cartFacade.cart$,
          this.cartFacade.accountID$,
          this.authenticationFacade.accountDetails$
        ),
        tap(([action, cartStartTime, cart, accountID, accountDetails]) => {
          if (cart?.length && cartStartTime && accountID) {
            const oldDateTime = Date.parse(cartStartTime);
            const currentDateTime = Date.parse(new Date().toString());
            const differenceTime = currentDateTime - oldDateTime;
            const pastHours = Math.floor(differenceTime / 1000 / 60 / 60);
            if (pastHours > 5) {
              const okButton = {
                text: 'OK',
                cssClass: 'okButton centerButton',
                // handler: () => {
                //   this.authFacade.goHomeSuccess();
                // },
              };

              this.cartFacade.loadCart([]);
              this.cartFacade.clearState('cart');
              this.cartFacade.clearState('cartStartTime');
              this.chooseLocationFacade.clearState('acceptedAgeRestrictionYears');

              this.navController.navigateRoot(SELECT_APP_SIMPLE_TABS);

              if (accountDetails) {
                this.alertService.presentAlert('Alert', 'Due to inactivity, your basket has been emptied.', [okButton], false);
              }
            }
          }
        })
      ),
    { dispatch: false }
  );
}
